// PartySocket wiring — single connection per page.
// PLAN.md §7.1, §7.7. Reconnect with jittered exponential backoff.

import PartySocket from 'partysocket'
import { WS_CODES } from '@trading-grounds/shared'
import type { Cell, ClientMessage, ServerMessage } from '@trading-grounds/shared'
import { clearIdentity, saveIdentity, type Identity } from './identity.js'
import { useStore } from './store.js'

// In dev, Vite proxies /parties/* to wrangler at :8787 (see vite.config.ts).
// In prod, the client is served from the same origin as the worker.
const HOST = window.location.host
const PARTY = 'coliseum'
const ROOM = 'global'

let socket: PartySocket | null = null
let connectArgs: ConnectArgs | null = null

export type ConnectArgs = {
  display_handle: string
  /** auth_token from a prior session (optional). */
  token?: string
}

export function connect(args: ConnectArgs): PartySocket {
  // Idempotent: in React StrictMode the connect effect runs twice on mount.
  // If we're already connecting/connected with the same identity, no-op.
  if (
    socket &&
    connectArgs &&
    connectArgs.display_handle === args.display_handle &&
    connectArgs.token === args.token
  ) {
    return socket
  }
  if (socket) socket.close()
  connectArgs = args

  const query: Record<string, string> = { handle: args.display_handle }
  if (args.token) query.token = args.token

  const ws = new PartySocket({
    host: HOST,
    party: PARTY,
    room: ROOM,
    query,
    minReconnectionDelay: 1_000,
    maxReconnectionDelay: 30_000,
  })

  useStore.getState().setStatus('connecting')

  ws.addEventListener('open', () => {
    useStore.getState().setStatus('connected')
  })

  ws.addEventListener('close', (e) => {
    if (e.code === WS_CODES.REPLACED) {
      useStore.getState().setStatus('replaced')
      // Don't reconnect — the new tab owns the session.
      ws.close()
      return
    }
    if (e.code === WS_CODES.AUTH_INVALID) {
      // Stale token / handle taken / invalid handle — wipe local identity so
      // the picker can reappear.
      clearIdentity()
      useStore.getState().setStatus('auth_failed')
      ws.close()
      return
    }
    if (e.code === WS_CODES.D1_UNAVAILABLE) {
      useStore.getState().setStatus('reconnecting')
      // partysocket auto-reconnects; keep token in localStorage.
      return
    }
    useStore.getState().setStatus('reconnecting')
  })

  ws.addEventListener('message', (event) => {
    let msg: ServerMessage
    try {
      msg = JSON.parse(typeof event.data === 'string' ? event.data : '') as ServerMessage
    } catch {
      return
    }
    if (msg.type === 'welcome') {
      const identity: Identity = {
        handle: msg.handle,
        display_handle: msg.display_handle,
        auth_token: msg.auth_token,
      }
      saveIdentity(identity)
    }
    useStore.getState().applyServerMessage(msg)
  })

  socket = ws

  // PLAN.md §7.12 — visibility/sleep handling.
  if (!visibilityHooked) {
    document.addEventListener('visibilitychange', onVisibilityChange)
    visibilityHooked = true
  }

  return ws
}

let visibilityHooked = false

function onVisibilityChange() {
  if (document.visibilityState !== 'visible') return
  // Drop any optimistic flag state — wait for the next live tick + roster.
  useStore.getState().clearOptimisticMove()
}

export function disconnect() {
  socket?.close()
  socket = null
}

export function sendMove(target: Cell): void {
  if (!socket) return
  if (target === 'spectator') {
    sendClientMessage({ type: 'bank' })
  } else {
    sendClientMessage({ type: 'move', to_cell: target })
  }
  useStore.getState().beginOptimisticMove(target)
}

export function fetchPriceHistory(window: '1h' | '4h'): void {
  sendClientMessage({ type: 'fetch_price_history', window })
}

function sendClientMessage(msg: ClientMessage): void {
  if (!socket) return
  socket.send(JSON.stringify(msg))
}
